import { useMemo } from 'react'

import { DayV2 } from '@library/api-deliveries'

export type CalendarDeliverDay = {
  id: string
  slotId: string
  date: string
  defaultDay: number
  disabled?: boolean
}

const formatDate = (date: Date) => date.toISOString().split('T')[0]

const getBasicDays = (days: DayV2[]) =>
  days.map((day) => ({
    id: day.id,
    date: day.date,
    defaultDay: day.daySlotLeadTimes[0].defaultDay,
    slotId: day.daySlotLeadTimes[0].slotId,
    disabled: false,
  }))

const addNonDeliveryDays = (days: CalendarDeliverDay[], initialDay: string, lastDay: string) => {
  const calendarDates: CalendarDeliverDay[] = []
  const lastDate = new Date(lastDay)

  for (let day = new Date(initialDay); day <= lastDate; day.setDate(day.getDate() + 1)) {
    const date = formatDate(day)

    const foundDate = days.find((dayV2) => dayV2.date === date)

    if (foundDate) {
      calendarDates.push(foundDate)
    } else {
      calendarDates.push({
        id: '',
        date,
        defaultDay: 0,
        slotId: '',
        disabled: true,
      })
    }
  }

  return calendarDates
}

const getFirstAndLastDeliveryOptionDate = (calendarDays: CalendarDeliverDay[]) => ({
  initialDate: calendarDays[0].date,
  lastDate: calendarDays[calendarDays.length - 1].date,
})

export const useSubscriptionCalendar = (days: DayV2[] | null) => {
  const getCalendarWeekDays = () => ['M', 'T', 'W', 'T', 'F', 'S', 'S']

  const initialState = useMemo(
    () => ({
      getCalendarWeekDays,
      getCalendarDays: () => [],
      getInitialDate: () => '',
      getLastDate: () => '',
    }),
    [],
  )

  if (!days || !days.length) {
    return initialState
  }
  const filteredDays = getBasicDays(days)
  const { initialDate, lastDate } = getFirstAndLastDeliveryOptionDate(filteredDays)
  const calendarDates = addNonDeliveryDays(filteredDays, initialDate, lastDate)

  const getWeekDayForInitialSlot = () => {
    const weekDay = new Date(days[0].date).getDay()

    return weekDay === 0 ? 6 : weekDay - 1
  }

  const getCalendarDays = () => {
    const addEmptyDays = new Array(getWeekDayForInitialSlot()).fill(null)
    const addDaysFromSlots = addEmptyDays.concat(calendarDates)

    const groupDaysByWeek = addDaysFromSlots.reduce(
      (prev: CalendarDeliverDay[][], current: CalendarDeliverDay, index: number) => {
        if (index % getCalendarWeekDays().length === 0) {
          prev.push([])
        }

        prev[prev.length - 1].push(current)

        return prev
      },
      [],
    ) as CalendarDeliverDay[][]

    return groupDaysByWeek
  }

  return {
    getCalendarWeekDays,
    getCalendarDays,
    getInitialDate: () => initialDate,
    getLastDate: () => lastDate,
  }
}
